import { FamilyApplicationResp, Student, StudentInception } from '@/apis/family_application/requests'
import { AppRoutePaths } from '@/routing/route_paths'
import { ProgramSettingsResp } from '@/storage/program_settings/requests'
import { ApplicationStep } from './ApplicationStepper'
import { ENV } from '@/common/constants.ts'

export type StepCode =
  | 'identityVerification'
  | 'residencyVerification'
  | 'students'
  | 'incomeVerification'
// | 'attestations'

// This is a temporary fix to make the dashboard progress bar and the application stepper work together.
export type ExtendedApplicationStep = ApplicationStep & {
  code: StepCode
}

// Don't export this one.
// Use the getApplicationSteps function to get the correct steps based on the program settings.
export const ApplicationSteps: ExtendedApplicationStep[] = [
  {
    name: 'identity_verification',
    code: 'identityVerification',
    title_translation_key: 'identity_verification',
    location: AppRoutePaths.family_application.identity_verification.index,
  },
  {
    name: 'residency_verification',
    code: 'residencyVerification',
    title_translation_key: 'Residency',
    location: AppRoutePaths.family_application.residency_verification.index,
  },
  {
    name: 'student_details',
    code: 'students',
    title_translation_key: 'student_details',
    location: AppRoutePaths.family_application.student_details.index,
  },
  {
    name: 'income_verification',
    title_translation_key: 'income_verification',
    code: 'incomeVerification',
    location: AppRoutePaths.family_application.income_verification.index,
  },
  // {
  //   name: 'attestations',
  //   title_translation_key: 'attestations',
  //   code: 'attestations',
  //   location: AppRoutePaths.family_application.attestations.index,
  // },
] as const

export function getUniqueApplicationSteps(
  programSettings: ProgramSettingsResp,
  students: Student[]
): ExtendedApplicationStep[] {
  const mapSteps: Map<string, ExtendedApplicationStep> = new Map()

  if (students.length > 0) {
    students.forEach((student) => {
      const steps = getStudentApplicationSteps(
        programSettings,
        student.inception_status
      )
      steps.forEach((step) => {
        if (!mapSteps.has(step.code)) {
          mapSteps.set(step.code, step)
        }
      })
    })
  } else {
    return getStudentApplicationSteps(programSettings)
  }
  return Array.from(mapSteps.values())
}

export function getStudentApplicationSteps(
  programSettings: ProgramSettingsResp | null,
  studentInception?: StudentInception
): ExtendedApplicationStep[] {
  if (studentInception && skipRenewalIncomeVerification(programSettings, studentInception)) {
    return ApplicationSteps.filter((item) => item.code !== 'incomeVerification')
  }

  return ApplicationSteps
}

/**
 * Determines whether to skip the income verification process for a renewal applicant.
 *
 * @param programSettings - The settings for the current program, which may include whether income verification is required for renewals.
 * @param studentInception - The current status of the application, used to determine if the applicant is a renewal applicant.
 * @return Returns true if the applicant is a renewal applicant and renewal income verification is not required, false otherwise.
 */
const renewalApplicantStatuses: StudentInception[] = ['Renewal Student']


export function skipRenewalIncomeVerification(
  programSettings: ProgramSettingsResp | null,
  studentInception: StudentInception
) {

  const isRenewalApplicant = renewalApplicantStatuses.includes(studentInception)
  const requiresRenewalIncome = programSettings?.require_renewal_income

  return isRenewalApplicant && !requiresRenewalIncome
}

export function isRenewalApplicant(application: FamilyApplicationResp){ 
  return application.students.some((student) =>
    renewalApplicantStatuses.includes(student.inception_status)
  )
}


export const LOG_TYPES = {
  REGISTER: 'register',
  START: 'start',
  VIEW_PAGE: 'view_page',
  UPDATE: 'update',
  ADD: 'add',
  UPLOAD_DOCUMENT: 'upload_document',
  DELETE: 'delete',
  FAILED: 'failed',
} as const

export const getPrivacyPolicyUrl = (lang: string) => {
  if (lang === 'en') {
    return `${ENV.VITE_API_URL}/Files/ACE_Privacy_Policy_2024-12-13.pdf`
  }
  return `${ENV.VITE_API_URL}/Files/ACE_Politica_de_Privacidad_2025-01-06.pdf`
}
